import {
  ClientFilterValuesResponse,
  CreateTaskRequest,
  CreateTaskResponse,
  DeleteTaskResponse,
  EditNotePayloadType,
  KanbanQueryParams,
  KanbanResponse,
  ListViewQueryParams,
  ListViewResponse,
  PostNotePayloadType,
  RollupNumbersResponse,
  TaskDropdownsResponse,
  UpdateTaskRequest,
  UpdateTaskResponse,
} from 'model/tasks.model';
import axios from '../core/axios';
import { ApiConstants } from './api-constants';
import { HuddleApiConstants } from './huddle-api-constants';

export class TaskService {
  static getTaskDropdowns = async (): Promise<TaskDropdownsResponse> => {
    return await (
      await axios.get(HuddleApiConstants.TASKS_API.GET_TASK_DROPDOWNS.URL())
    ).data;
  };

  static getClientFilterValues = async (): Promise<ClientFilterValuesResponse> => {
    return await (
      await axios.get(HuddleApiConstants.TASKS_API.GET_CLIENT_FILTER_VALUES.URL())
    ).data;
  };

  static createTask = async (data: CreateTaskRequest): Promise<CreateTaskResponse> => {
    return await (
      await axios.post(HuddleApiConstants.TASKS_API.CREATE_TASK.URL(), data)
    ).data;
  };

  static updateTask = async (
    taskId: string,
    data: UpdateTaskRequest,
  ): Promise<UpdateTaskResponse> => {
    return await (
      await axios.patch(HuddleApiConstants.TASKS_API.UPDATE_TASK.URL(taskId), data)
    ).data;
  };

  static deleteTask = async (taskId: string): Promise<DeleteTaskResponse> => {
    return await (
      await axios.delete(HuddleApiConstants.TASKS_API.DELETE_TASK.URL(taskId))
    ).data;
  };

  static getKanban = async (queryParams?: KanbanQueryParams): Promise<KanbanResponse> => {
    const queryString = queryParams
      ? HuddleApiConstants.TASKS_API.GET_KANBAN.QUERY_PARAMS(
          queryParams as Record<string, string | number | boolean>,
        )
      : '';
    const url = queryString
      ? `${HuddleApiConstants.TASKS_API.GET_KANBAN.URL()}?${queryString}`
      : HuddleApiConstants.TASKS_API.GET_KANBAN.URL();
    return await (
      await axios.get(url)
    ).data;
  };

  static getListView = async (queryParams?: ListViewQueryParams): Promise<ListViewResponse> => {
    const queryString = queryParams
      ? HuddleApiConstants.TASKS_API.GET_LIST_VIEW.QUERY_PARAMS(
          queryParams as Record<string, string | number | boolean>,
        )
      : '';
    const url = queryString
      ? `${HuddleApiConstants.TASKS_API.GET_LIST_VIEW.URL()}?${queryString}`
      : HuddleApiConstants.TASKS_API.GET_LIST_VIEW.URL();
    return await (
      await axios.get(url)
    ).data;
  };

  static getRollupNumbers = async (queryParams?: {
    mine?: boolean;
  }): Promise<RollupNumbersResponse> => {
    const queryString = queryParams
      ? HuddleApiConstants.TASKS_API.GET_ROLLUP_NUMBERS.QUERY_PARAMS(queryParams)
      : '';
    const url = queryString
      ? `${HuddleApiConstants.TASKS_API.GET_ROLLUP_NUMBERS.URL()}?${queryString}`
      : HuddleApiConstants.TASKS_API.GET_ROLLUP_NUMBERS.URL();
    return await (
      await axios.get(url)
    ).data;
  };

  static getPresignedUrl = async (params: {
    mailId: string;
    key: string;
    contentType: string;
    expires?: number;
  }): Promise<{ url: string; fields: Record<string, string> } | string> => {
    const queryString = HuddleApiConstants.TASKS_API.GET_PRESIGNED_URL.QUERY_PARAMS(params);
    const url = `${HuddleApiConstants.TASKS_API.GET_PRESIGNED_URL.URL()}?${queryString}`;
    return await (
      await axios.get(url)
    ).data;
  };

  static getTaskById = async (taskId: string) => {
    return await (
      await axios.get(HuddleApiConstants.TASKS_API.GET_TASK_BY_ID.URL(taskId))
    ).data;
  };

  static getNotes = async (taskId: string) => {
    return await (
      await axios.get(ApiConstants.TASKS_NOTES_API.GET_NOTES.URL(taskId))
    ).data;
  };

  static postNote = async (payload: PostNotePayloadType) => {
    return await (
      await axios.post(ApiConstants.TASKS_NOTES_API.POST_NOTE.URL(), payload)
    ).data;
  };

  static editNote = async (payload: EditNotePayloadType) => {
    return await (
      await axios.patch(ApiConstants.TASKS_NOTES_API.PATCH_NOTE.URL(), payload)
    ).data;
  };

  static deleteNote = async (noteId: string) => {
    return await (
      await axios.delete(ApiConstants.TASKS_NOTES_API.DELETE_NOTE.URL(noteId))
    ).data;
  };
}
